import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { TCComponentRawMaterialInformation } from "../../../../tc-models/tc-req-dto/tc-component-details.req_dto";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useParams } from "react-router-dom";

export interface ProductDerivedComponent {
  componentNetShippingWeight: number;
  componentSupplementaryWeight: number;
  componentCertifiedWeight: number;
  componentCertifiedAndNonCertifiedWeight?: number | null;
  rawMaterialInfo?: TCComponentRawMaterialInformation[];
}

/** Backend-calculated totals from GET product detail (after recompute) */
export interface BackendTotals {
  totalNetShippingWeight?: number | null;
  totalSupplementaryWeight?: number | null;
  totalDerivedWeight?: number | null;
  totalCertifiedWeight?: number | null;
  totalNonCertifiedWeight?: number | null;
}

/** One row for Product Raw Material and Composition (from backend or derived from components). Accepts both number/string for compatibility with ITcProductRawMaterialInformation. */
export interface BackendRawMaterialRow {
  tcProductRawMaterialOrder?: number | string;
  tcProductRawMaterialCode?: string;
  tcProductRawMaterialPercentage?: number | string;
  tcProductRawMaterialUserSpecificTerm?: string;
}

interface ProductInfoCompProps {
  shipmentNumber: string;
  productNumber: string;
  components: ProductDerivedComponent[];
  /** When provided, used for "Product Total Derived From Components" table instead of computing from components */
  backendTotals?: BackendTotals | null;
  /** When provided and non-empty, used for "Product Raw Material and Composition Information" table */
  backendRawMaterialInfo?: BackendRawMaterialRow[] | null;
}

const ProductInfoComp: React.FC<ProductInfoCompProps> = ({
  shipmentNumber,
  productNumber,
  components,
  backendTotals,
  backendRawMaterialInfo,
}) => {
  const { executeGet } = useApi();
  const { TE_ID } = useParams();

  const formatDynamicDecimals = (value: any) => {
    const numeric = Number(value ?? 0);
    if (Number.isNaN(numeric)) return "0";

    if (numeric === 0) return "0.00";

    let str = numeric.toFixed(5);
    str = str.replace(/0+$/, "");
    if (str.endsWith(".")) str += "0";

    return str;
  };
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<
    { standard: string; material_code: string }[]
  >([]);
  // null = not loaded yet; [] = loaded but empty (shouldn't happen, but safe)
  const [tcStandardList, setTcStandardList] = useState<any[] | null>(null);

  // Fetch certified raw material codes (same API/logic as component raw material screen)
  useEffect(() => {
    const fetchCertifiedRawMaterials = async () => {
      try {
        const response = await executeGet(
          TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES
        );
        const raw = (response as any)?.data;
        const list = Array.isArray(raw)
          ? raw
          : raw?.data && Array.isArray(raw.data)
          ? raw.data
          : [];
        if (list.length > 0) {
          setCertifiedRawMaterialCodes(list);
        }
      } catch (err) {
        Utills.cle?.(`Error fetching certified raw material codes in ProductInfoComp ${err}`);
      }
    };
    fetchCertifiedRawMaterials();
  }, []);

  useEffect(() => {
    const fetchTcStandard = async () => {
      if (!TE_ID) return;
      try {
        const response: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS, {
          tcId: TE_ID,
        });
        if (response?.data?.tcStandard) {
          setTcStandardList(response.data.tcStandard);
        }
      } catch (err) {
        console.error("Failed to fetch TC base details for standards:", err);
      }
    };

    fetchTcStandard();
  }, []);

  const isCertifiedRawMaterialCode = (
    materialCode: string
  ): boolean | null => {
    const standard =
      certifiedRawMaterialCodes.find((item) => item.material_code === materialCode)
        ?.standard || "";

    if (!standard) return false;

    // If backend marks it as not certified (NA), always treat as No.
    if (String(standard).toUpperCase() === "NA") return false;

    // Wait until TC standards are loaded.
    if (tcStandardList === null) return null;
    if (!tcStandardList.length) return false;

    return tcStandardList.some((std: any) => String(standard).includes(std));
  };

  const totalsFromComponents = useMemo(() => {
    const netShipping = components.reduce(
      (sum, c) => sum + (Number(c.componentNetShippingWeight) || 0),
      0
    );
    const supplementary = components.reduce(
      (sum, c) => sum + (Number(c.componentSupplementaryWeight) || 0),
      0
    );
    const certifiedNonCertified = components.reduce(
      (sum, c) =>
        sum + (Number(c.componentCertifiedAndNonCertifiedWeight) || 0),
      0
    );
    const certified = components.reduce(
      (sum, c) => sum + (Number(c.componentCertifiedWeight) || 0),
      0
    );
    const nonCertified = certifiedNonCertified - certified;
    return {
      netShippingWeight: netShipping,
      supplementaryWeight: supplementary,
      certifiedNonCertifiedWeight: certifiedNonCertified,
      certifiedWeight: certified,
      nonCertifiedWeight: nonCertified >= 0 ? nonCertified : 0,
    };
  }, [components]);

  const totals = useMemo(() => {
    if (
      backendTotals &&
      (backendTotals.totalNetShippingWeight != null ||
        backendTotals.totalCertifiedWeight != null)
    ) {
      return {
        netShippingWeight: Number(backendTotals.totalNetShippingWeight) || 0,
        supplementaryWeight: Number(backendTotals.totalSupplementaryWeight) || 0,
        certifiedNonCertifiedWeight: Number(backendTotals.totalDerivedWeight) || 0,
        certifiedWeight: Number(backendTotals.totalCertifiedWeight) || 0,
        nonCertifiedWeight: Number(backendTotals.totalNonCertifiedWeight) || 0,
      };
    }
    return totalsFromComponents;
  }, [backendTotals, totalsFromComponents]);

  const rawMaterialRowsFromComponents = useMemo(() => {
    const byCode: Record<
      string,
      { weight: number; certified: string }
    > = {};
    components.forEach((comp) => {
      (comp.rawMaterialInfo || []).forEach((rm) => {
        const code = rm.tcComponentRawMaterialCode || "";
        const w = Number(rm.tcComponentRawMaterialAllocatedWeight) || 0;
        if (!code) return;
        if (!byCode[code]) {
          byCode[code] = { weight: 0, certified: rm.tcComponentRawMaterialCertified || "Yes" };
        }
        byCode[code].weight += w;
        if (rm.tcComponentRawMaterialCertified === "No") byCode[code].certified = "No";
      });
    });
    return Object.entries(byCode).map(([rawMaterial, { weight, certified }]) => ({
      rawMaterial: rawMaterial,
      certified,
      rawMaterialPct: weight / 10,
      rawMaterialWeight: weight,
    }));
  }, [components]);

  const rawMaterialRows = useMemo(() => {
    // For products with components, prefer backend-aggregated composition derived from components,
    // since it carries correct certified/non-certified flags and allocated weights.
    if (rawMaterialRowsFromComponents.length > 0) {
      return rawMaterialRowsFromComponents;
    }

    // Fallback: use backend raw material info (mainly for products without components).
    if (Array.isArray(backendRawMaterialInfo) && backendRawMaterialInfo.length > 0) {
      const totalDerived = totals.certifiedNonCertifiedWeight || 0;
      return backendRawMaterialInfo.map((row) => {
        const code = row.tcProductRawMaterialCode ?? "";
        const pct = Number(row.tcProductRawMaterialPercentage) || 0;
        const weight =
          totalDerived > 0 ? (totalDerived * pct) / 100 : 0;
        return {
          rawMaterial: code,
          certified: (() => {
            if (!code) return "-";
            const res = isCertifiedRawMaterialCode(code);
            if (res === null) return "-";
            return res ? "Yes" : "No";
          })(),
          rawMaterialPct: pct,
          rawMaterialWeight: weight,
        };
      });
    }

    return [];
  }, [
    backendRawMaterialInfo,
    rawMaterialRowsFromComponents,
    totals.certifiedNonCertifiedWeight,
    certifiedRawMaterialCodes,
    tcStandardList,
  ]);

  const weightTableData = [
    { weightType: "Net Shipping Weight", weight: totals.netShippingWeight },
    { weightType: "Supplementary Weight", weight: totals.supplementaryWeight },
    {
      weightType: "Certified + Non-Certified Weight",
      weight: totals.certifiedNonCertifiedWeight,
    },
    { weightType: "Certified Weight", weight: totals.certifiedWeight },
    { weightType: "Non-Certified Weight", weight: totals.nonCertifiedWeight },
  ];

  return (
    <Box sx={{ flexGrow: 1, p: 0 }}>
      {/* Product Total Derived From Components */}
      <Box sx={{ mb: 4, maxWidth: 420 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 1, color: "#333" }}
        >
          Product Total Derived From Components
        </Typography>
        <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
          Associated Shipment Number: {shipmentNumber} Product Number:{" "}
          {productNumber}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <CustomTable
            columns={[
              { accessorKey: "weightType", header: "Weight Type", minSize: 120, maxSize: 280 },
              {
                accessorKey: "weight",
                header: "Weight (kg)",
                minSize: 80,
                maxSize: 120,
                Cell: ({ row }: any) => formatDynamicDecimals(row?.original?.weight),
              },
            ]}
            data={weightTableData}
            enableRowActions={false}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableSorting={false}
          />
        </Box>
      </Box>

      {/* Product Raw Material and Composition Information */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 2, color: "#333" }}
        >
          Product Raw Material and Composition Information
        </Typography>
        <CustomTable
          columns={[
            { accessorKey: "rawMaterial", header: "Raw Material", minSize: 100, maxSize: 150 },
            { accessorKey: "certified", header: "Certified", minSize: 80, maxSize: 120 },
            { accessorKey: "rawMaterialPct", header: "Raw Material %", minSize: 100, maxSize: 140 },
            {
              accessorKey: "rawMaterialWeight",
              header: "Raw Material Weight",
              minSize: 120,
              maxSize: 160,
              Cell: ({ row }: any) => formatDynamicDecimals(row?.original?.rawMaterialWeight),
            },
          ]}
          data={rawMaterialRows}
          enableRowActions={false}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enableSorting={false}
        />
      </Box>
    </Box>
  );
};

export default ProductInfoComp;
